'use client';

import * as Sentry from '@sentry/nextjs';
import {
  PHI_ROUTE_PATTERNS,
  SAFE_SENTRY_CAPTURE_TAG,
  scrubPHI,
  scrubPHIString,
} from './sentry-scrub';
import { useAuthStore } from '@/stores/auth-store';

type CaptureSource =
  | 'client_window_error'
  | 'client_unhandled_rejection'
  | 'next_error_boundary'
  | 'react_query_error'
  | 'react_mutation_error';

type ClientCaptureContext = {
  source: CaptureSource;
  boundary?: string;
  operation?: string;
  queryHash?: string;
  queryKey?: unknown;
  mutationKey?: unknown;
  status?: string;
  failureCount?: number;
  errorDigest?: string;
  resourceTag?: string;
  resourceUrl?: string;
};

const UUID_RE = /\b[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}\b/gi;
const SUPABASE_TRANSPORT_FAILURE_RE =
  /(?:load failed|failed to fetch|network(?: request)? failed|networkerror)[\s\S]*(?:\.supabase\.co|supabase)/i;
let handlersInstalled = false;
let identitySubscriptionInstalled = false;

function currentPath(): string {
  if (typeof window === 'undefined') return '/unknown';
  return window.location.pathname || '/unknown';
}

function isPhiRoute(path: string): boolean {
  const url = `https://app.statura.care${path.startsWith('/') ? path : `/${path}`}`;
  return PHI_ROUTE_PATTERNS.some((re) => re.test(url));
}

function safeSentryText(value: string): string {
  return scrubPHIString(value).replace(UUID_RE, '[id]');
}

function safeTagValue(value: string): string {
  return safeSentryText(value).slice(0, 200);
}

function stringProperty(value: unknown, key: string): string | undefined {
  if (!value || typeof value !== 'object') return undefined;
  const candidate = (value as Record<string, unknown>)[key];
  return typeof candidate === 'string' && candidate.trim().length > 0
    ? candidate.trim()
    : undefined;
}

export function sanitizeRoutePathForSentry(path: string): string {
  const cleanPath = (path || '/unknown').split('?')[0]?.split('#')[0] || '/unknown';
  const scrubbedPath = safeSentryText(cleanPath);
  if (!isPhiRoute(cleanPath)) return scrubbedPath;
  const parts = scrubbedPath.split('/').filter(Boolean);
  if (parts.length === 0) return '/';
  if (parts[0] === 'api') return `/${parts.slice(0, 2).join('/')}`;
  return `/${parts.slice(0, 2).join('/')}`;
}

export function clientOperationalErrorFromUnknown(error: unknown): Error {
  if (isSupabaseTransportFailure(error)) {
    return new Error('Supabase connection temporarily unavailable');
  }
  if (error instanceof Error) return error;
  if (typeof error === 'string' && error.trim().length > 0) return new Error(error);
  for (const key of ['message', 'error', 'details', 'hint', 'code']) {
    const message = stringProperty(error, key);
    if (message) return new Error(message);
  }
  return new Error('Client runtime error with no browser payload');
}

function errorSearchText(error: unknown): string {
  if (error instanceof Error) return `${error.name} ${error.message}`;
  if (typeof error === 'string') return error;
  if (!error || typeof error !== 'object') return '';

  return ['message', 'error', 'details', 'hint', 'url']
    .map((key) => stringProperty(error, key))
    .filter((value): value is string => Boolean(value))
    .join(' ');
}

export function isSupabaseTransportFailure(error: unknown): boolean {
  return SUPABASE_TRANSPORT_FAILURE_RE.test(errorSearchText(error));
}

function safeResourceUrl(value: string | null | undefined): string | undefined {
  if (!value || typeof window === 'undefined') return undefined;

  try {
    const url = new URL(value, window.location.origin);
    if (url.origin !== window.location.origin) return '[external]';
    if (url.pathname.startsWith('/_next/static/')) return url.pathname;
    if (url.pathname === '/favicon.ico' || url.pathname === '/manifest.webmanifest') {
      return url.pathname;
    }

    const extension = url.pathname.match(/\.[a-z0-9]{2,8}$/i)?.[0]?.toLowerCase();
    return extension ? `[same-origin]${extension}` : '[same-origin]';
  } catch {
    return undefined;
  }
}

function describeResourceError(event: Event): {
  error: Error;
  resourceTag: string;
  resourceUrl?: string;
} | null {
  const target = event.target;
  if (!(target instanceof HTMLElement)) return null;

  const resourceTag = target.tagName.toLowerCase();
  const rawUrl =
    target instanceof HTMLScriptElement
      ? target.src
      : target instanceof HTMLLinkElement
        ? target.href
        : target instanceof HTMLImageElement
          ? target.currentSrc || target.src
          : undefined;

  return {
    error: new Error(`Client resource failed to load: ${resourceTag}`),
    resourceTag,
    resourceUrl: safeResourceUrl(rawUrl),
  };
}

function safeContext(context: ClientCaptureContext): Record<string, unknown> {
  return scrubPHI({
    ...context,
    queryHash: context.queryHash ? safeSentryText(context.queryHash) : undefined,
    queryKey: context.queryKey ? safeSentryText(JSON.stringify(context.queryKey)) : undefined,
    mutationKey: context.mutationKey
      ? safeSentryText(JSON.stringify(context.mutationKey))
      : undefined,
  }) as Record<string, unknown>;
}

function operationFamilyTag(value: unknown): string | undefined {
  if (Array.isArray(value) && typeof value[0] === 'string') return safeTagValue(value[0]);
  if (typeof value === 'string') return safeTagValue(value);
  return undefined;
}

export function captureClientOperationalError(error: unknown, context: ClientCaptureContext): void {
  const path = currentPath();
  const routeClass = sanitizeRoutePathForSentry(path);
  const phiRoute = isPhiRoute(path);
  const baseError = clientOperationalErrorFromUnknown(error);
  const supabaseTransportFailure = isSupabaseTransportFailure(error);
  const capturedError = phiRoute
    ? new Error(scrubPHIString(baseError.message || 'Client error on protected route'))
    : baseError;

  Sentry.withScope((scope) => {
    scope.setLevel('error');
    scope.setTag('source', context.source);
    scope.setTag('route_class', routeClass);
    scope.setTag('route_path', routeClass);
    if (context.boundary) scope.setTag('error_boundary', context.boundary);
    if (context.operation) scope.setTag('operation', context.operation);
    const queryFamily = operationFamilyTag(context.queryKey);
    const mutationFamily = operationFamilyTag(context.mutationKey);
    if (queryFamily) scope.setTag('query_family', queryFamily);
    if (mutationFamily) scope.setTag('mutation_family', mutationFamily);
    if (context.status) scope.setTag('query_status', safeTagValue(context.status));
    if (typeof context.failureCount === 'number') {
      scope.setTag('query_failure_count', String(context.failureCount));
    }
    if (supabaseTransportFailure) {
      scope.setTag('failure_class', 'supabase_transport');
      scope.setTag('external_service', 'supabase');
      scope.setTag('transient', 'true');
    }
    if (phiRoute) scope.setTag(SAFE_SENTRY_CAPTURE_TAG, 'true');
    scope.setContext('statura_client_error', safeContext(context));
    Sentry.captureException(capturedError, {
      mechanism: {
        handled:
          context.source !== 'client_window_error' &&
          context.source !== 'client_unhandled_rejection',
        type: context.source,
      },
    });
  });
}

function syncClientSentryIdentity(): void {
  const user = useAuthStore.getState().user;
  const scope = Sentry.getCurrentScope();
  if (!user) {
    scope.setUser(null);
    scope.setTag('tenant_id', undefined);
    scope.setTag('user_role', undefined);
    return;
  }

  scope.setUser({ id: user.id });
  if (user.tenantId) scope.setTag('tenant_id', user.tenantId);
  else scope.setTag('tenant_id', undefined);
  if (user.role) scope.setTag('user_role', safeTagValue(user.role));
  else scope.setTag('user_role', undefined);
}

function installClientSentryIdentityTelemetry(): void {
  if (identitySubscriptionInstalled) return;
  identitySubscriptionInstalled = true;
  syncClientSentryIdentity();
  useAuthStore.subscribe((state, previousState) => {
    if (
      state.user?.id === previousState.user?.id &&
      state.user?.tenantId === previousState.user?.tenantId &&
      state.user?.role === previousState.user?.role
    ) {
      return;
    }
    syncClientSentryIdentity();
  });
}

export function captureNextErrorBoundary(
  error: Error & { digest?: string },
  boundary: string,
): void {
  captureClientOperationalError(error, {
    source: 'next_error_boundary',
    boundary,
    errorDigest: error.digest,
  });
}

export function captureReactQueryError(
  error: unknown,
  context: Omit<ClientCaptureContext, 'source'> & {
    source?: 'react_query_error' | 'react_mutation_error';
  },
): void {
  captureClientOperationalError(error, {
    source: context.source ?? 'react_query_error',
    operation: context.operation,
    queryHash: context.queryHash,
    queryKey: context.queryKey,
    mutationKey: context.mutationKey,
    status: context.status,
    failureCount: context.failureCount,
  });
}

export function installClientErrorTelemetry(): void {
  if (handlersInstalled || typeof window === 'undefined') return;
  handlersInstalled = true;
  installClientSentryIdentityTelemetry();

  window.addEventListener(
    'error',
    (event: Event) => {
      if (event instanceof ErrorEvent) {
        captureClientOperationalError(event.error ?? event.message, {
          source: 'client_window_error',
          operation: 'window.error',
        });
        return;
      }

      const resourceError = describeResourceError(event);
      if (!resourceError) return;

      captureClientOperationalError(resourceError.error, {
        source: 'client_window_error',
        operation: 'window.resource_error',
        resourceTag: resourceError.resourceTag,
        resourceUrl: resourceError.resourceUrl,
      });
    },
    { capture: true },
  );

  window.addEventListener('unhandledrejection', (event) => {
    captureClientOperationalError(event.reason, {
      source: 'client_unhandled_rejection',
      operation: 'window.unhandledrejection',
    });
  });
}
